import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { CustomerDocument, CustomerDocumentKind } from "../../shared/customer-documents";
import {
  changeCustomerLibraryPublication,
  previewCustomerLibraryDocument,
} from "./customer-documents-api";
export type PublicationAction = "publish" | "unpublish" | "archive" | "restore";
export type CustomerDocumentDialogProps = {
  slug: string;
  customerName: string;
  kind: CustomerDocumentKind;
  initial?: CustomerDocument;
  canWrite: boolean;
  canUsePages: boolean;
  onClose: () => void;
};
const notices = {
  publish: "Shared. They can open it from their workspace.",
  unpublish: "No longer shared. Your team’s copy is kept.",
  restore: "Restored. Only your team can see it.",
  archive: "Archived. Only your team can see it, under Archived.",
};
export type Confirmation = "archive" | "unpublish" | "discard" | "discardEdit";

function resolveConfirmation(
  confirmation: Confirmation | null,
  to: { close: () => void; stopEditing: () => void; act: (value: "archive" | "unpublish") => void },
) {
  if (confirmation === "discard") to.close();
  else if (confirmation === "discardEdit") to.stopEditing();
  else if (confirmation) to.act(confirmation);
}
function useDocumentPreview(
  slug: string,
  document: CustomerDocument | undefined,
  editing: boolean,
) {
  return useQuery({
    queryKey: ["ops", "customer-document-preview", slug, document?.id, document?.revision],
    queryFn: () => previewCustomerLibraryDocument(slug, document?.id ?? ""),
    enabled: !!document && !editing,
    staleTime: 0,
    gcTime: 0,
  });
}

function usePublication(
  slug: string,
  document: CustomerDocument | undefined,
  onChanged: (document: CustomerDocument, value: PublicationAction) => void,
) {
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: ["ops", "customer-library", slug] });
  const action = useMutation({
    mutationFn: (value: PublicationAction) =>
      changeCustomerLibraryPublication(
        slug,
        { id: document?.id ?? "", revision: document?.revision ?? 0 },
        value,
      ),
    onSuccess: async (result, value) => {
      onChanged(result.document, value);
      await refresh();
    },
  });
  return { action, refresh };
}

export function useCustomerDocumentDialog(props: CustomerDocumentDialogProps) {
  const [document, setDocument] = useState(props.initial);
  const [editing, setEditing] = useState(!props.initial);
  const [previewing, setPreviewing] = useState(false);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [notice, setNotice] = useState("");
  const [dirty, setDirty] = useState(false);
  const [formBusy, setFormBusy] = useState(false);
  const preview = useDocumentPreview(props.slug, document, editing);
  const { action, refresh } = usePublication(props.slug, document, (result, value) => {
    setDocument(result);
    setConfirmation(null);
    setPreviewing(false);
    setNotice(notices[value]);
  });
  async function saved(value: CustomerDocument) {
    setDocument(value);
    setEditing(false);
    setPreviewing(false);
    setDirty(false);
    setNotice("Saved.");
    await refresh();
  }
  function close() {
    if (action.isPending || formBusy) return;
    if (dirty) setConfirmation("discard");
    else props.onClose();
  }
  /** Cancel while editing: a saved document goes back to reading it; a new one closes. */
  const cancelEdit = () => {
    if (!document) close();
    else if (!formBusy) {
      if (dirty) setConfirmation("discardEdit");
      else setEditing(false);
    }
  };
  const requestAction = (value: PublicationAction) => {
    if (value === "archive" || value === "unpublish") setConfirmation(value);
    else action.mutate(value);
  };
  const confirm = () => {
    if (confirmation === "discardEdit") setDirty(false);
    resolveConfirmation(confirmation, {
      close: props.onClose,
      stopEditing: () => {
        setEditing(false);
        setConfirmation(null);
      },
      act: (value) => {
        action.mutate(value);
      },
    });
  };
  return {
    document,
    editing,
    setEditing,
    previewing,
    setPreviewing,
    confirmation,
    setConfirmation,
    notice,
    preview,
    action,
    saved,
    close,
    cancelEdit,
    requestAction,
    confirm,
    setDirty,
    setFormBusy,
    busy: action.isPending || formBusy,
  };
}
export type CustomerDocumentDialogState = ReturnType<typeof useCustomerDocumentDialog>;
